/**
 * Featured entity class
 */

import { has } from 'app/helpers/object'

/**
 * FeaturedFactory
 * returns Featured entity class
 *
 * @class FeaturedFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Featured} class
 *
 * @ngdoc factory
 * @name mwApp.factory:FeaturedFactory
 */
/* @ngInject */
export default function FeaturedFactory ($injector, $sce, $http, Type, Entity, FeaturedCarousel, alertLiteSvc, onOffMixin) {
  /* === Featured class === */

  /**
   * Featured model
   *
   * @class Featured
   * @classdesc Featured model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.Featured} Featured entity
   */
  function Featured () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Featured.prototype = Object.create(Entity.prototype)
  Featured.prototype.constructor = Featured

  /* === Featured instance methods & fields === */

  Object.assign(Featured.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Featured#
     * @type {Object}
     */
    _fields: {
      networkId: Number,
      subscriptions: ['Subscription'],
      carousels: {
        VOD: 'FeaturedCarousel',
        TV_CHANNEL: 'FeaturedCarousel',
        TV_SHOW: 'FeaturedCarousel'
      },
      $listOfSubscriptions: ['Subscription'],
      $action: String
    },

    /**
     * url template
     *
     * @memberof models.Featured#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/subscriptions/carousels/featured/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Featured#
     * @type {String}
     */
    _publicUrlTemplate: 'networks/{{::networkId}}/subscriptions/carousels/featured/{{::id}}',

    /**
     * Prepare note
     *
     * @memberof models.Featured#
     * @override
     */
    _prepare: function () {},

    /**
     * Get underlying object
     *
     * @memberof models.Featured#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get banner content types
     *
     * @memberof models.Featured#
     */
    _getContentTypes: function () {
      if (!this._url) {
        return this._updateUrl()
          .then(() => this._getContentTypes())
      }

      return $http.get(this._url.slice(0, -`${this.id}`.length) + 'content-types')
        .then((response) => has('payload')(response.data)
          ? response.data.payload
          : response.data)
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Get banner platforms
     *
     * @memberof models.Featured#
     */
    _getContent: function (tableState, o) {
      if (!this._url) {
        return this._updateUrl()
          .then(() => this._getContentTypes())
      }

      var params = {
        'sort-by': tableState.sort ? tableState.sort : '',
        page: tableState.page || 0,
        count: tableState.count
      }

      if (Object.prototype.hasOwnProperty.call(tableState, 'q')) {
        params.q = tableState.q
      }

      return $http.get(this._url.slice(0, -`${this.id}`.length) + 'content', { params: Object.assign(params, o) })
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    }
  }, onOffMixin)

  /* === Featured static methods & fields === */

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  Featured.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Get available Subscriptions
   *
   * @param {Number} networkId
   * @return {Promise} promise
   */
  Featured.getAvailableSubscriptions = function (networkId) {
    return $http.get(`api/networks/${networkId}/subscriptions?carouselType=FEATURED`)
      .then((response) => response.data)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Find all platforms of package
   *
   * @param {Number} networkId
   * @param {[Number]} subscriptionIds
   * @return {Promise} promise
   */
  Featured.getPlatforms = function (networkId, subscriptionIds) {
    return $http.get(`api/networks/${networkId}/subscriptions/carousels/featured/platforms`, { params: { subscriptionIds } })
      .then((response) => response.data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Find all content types
   *
   * @param {Number} networkId
   * @return {Promise} promise
   */
  Featured.getContentTypes = function (networkId) {
    return $http.get(`api/networks/${networkId}/subscriptions/carousels/featured/content-types`)
      .then((response) => response.data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  Featured.deleteFeatured = function (networkId, subscriptionIds) {
    return $http.delete(`api/networks/${networkId}/subscriptions/carousels/featured`, { params: { subscriptionIds } })
  }

  // copy all static methods from Entity class
  Object.assign(Featured, Entity)
  Featured._name = 'Featured'
  Featured.init()

  // permissions
  Featured.access = {
    view: 'viewFeaturedCarousel',
    edit: 'editFeaturedCarousel',
    create: 'editFeaturedCarousel',
    delete: 'deleteFeaturedCarousel'
  }

  return Featured
}
